import { useState } from "react";
import { Loader2, Search, Merge, Trash2, Globe, BarChart3, Crown, Link2, Check, X, Mail, RefreshCw } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { tokens } from "@/design-system/tokens";

// Inline Button component
const Button = ({ onClick, disabled = false, children, variant = "primary", size = "sm", className = "", type = "button", style = {}, ...props }) => {
  const isPrimary = variant === "primary";
  const isGhost = variant === "ghost";
  const isOutline = variant === "outline";
  const isIcon = variant === "icon" || size === "icon";

  const baseStyle = {
    padding: isIcon ? "4px" : size === "sm" ? "6px 12px" : "8px 16px",
    fontSize: isIcon ? "inherit" : "12px",
    fontWeight: 500,
    borderRadius: tokens.radius.md,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s",
    opacity: disabled ? 0.6 : 1,
    width: isIcon ? "24px" : "auto",
    height: isIcon ? "24px" : "auto",
    justifyContent: isIcon ? "center" : "flex-start",
    ...style,
  };

  const primaryStyle = {
    ...baseStyle,
    backgroundColor: tokens.color.orange,
    color: tokens.color.text,
  };

  const ghostStyle = {
    ...baseStyle,
    backgroundColor: "transparent",
    color: tokens.color.textMuted,
  };

  const outlineStyle = {
    ...baseStyle,
    backgroundColor: "transparent",
    color: tokens.color.text,
    border: `1px solid ${tokens.color.innerBorder}`,
  };

  const finalStyle = isPrimary ? primaryStyle : isOutline ? outlineStyle : ghostStyle;

  return (
    <button onClick={onClick} disabled={disabled} style={finalStyle} type={type} {...props}>
      {children}
    </button>
  );
};

// Inline Label component
const Label = ({ htmlFor, children, className = "", ...props }) => (
  <label htmlFor={htmlFor} style={{ fontSize: "10px", color: tokens.color.textMuted, cursor: "pointer" }} {...props}>
    {children}
  </label>
);

// Inline Badge component
const Badge = ({ variant = "secondary", children, className = "" }) => {
  const isOutline = variant === "outline";
  const style = isOutline
    ? { fontSize: "10px", padding: "2px 6px", backgroundColor: tokens.color.amberSoft, color: tokens.color.amber, borderRadius: "4px", border: "1px solid rgba(202, 138, 4, 0.5)", display: "inline-flex", alignItems: "center", gap: "4px" }
    : { fontSize: "10px", padding: "2px 6px", backgroundColor: tokens.color.innerBg, color: tokens.color.textMuted, borderRadius: "4px", display: "inline-flex", alignItems: "center", gap: "4px" };
  return <span style={style}>{children}</span>;
};

// Inline Input component
const Input = ({ value, onChange, placeholder = "", type = "text", className = "", id = "", ...props }) => (
  <input
    id={id}
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={{
      height: "32px",
      fontSize: "12px",
      padding: "4px 8px",
      backgroundColor: tokens.color.innerBg,
      border: `1px solid ${tokens.color.innerBorder}`,
      borderRadius: "4px",
      color: tokens.color.text,
      width: "100%",
    }}
    {...props}
  />
);

// Inline Card components
const Card = ({ children, className = "", style = {}, ...props }) => (
  <div
    style={{
      backgroundColor: tokens.color.cardBg,
      border: `1px solid ${tokens.color.innerBorder}`,
      borderRadius: tokens.radius.lg,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

const CardHeader = ({ children, className = "", style = {}, ...props }) => (
  <div
    style={{
      padding: `${tokens.space.md}px`,
      borderBottom: `1px solid rgba(39, 39, 42, 0.5)`,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

const CardTitle = ({ children, className = "", style = {}, ...props }) => (
  <h3
    style={{
      fontSize: "12px",
      fontWeight: 500,
      color: tokens.color.text,
      ...style,
    }}
    {...props}
  >
    {children}
  </h3>
);

const CardContent = ({ children, className = "", style = {}, ...props }) => (
  <div
    style={{
      padding: `${tokens.space.md}px`,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

// Inline Dialog components
const Dialog = ({ open, onOpenChange, children }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={() => onOpenChange?.(false)}
    >
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
};

const DialogContent = ({ children, className = "", style = {}, ...props }) => (
  <div
    style={{
      backgroundColor: tokens.color.cardBg,
      border: `1px solid ${tokens.color.innerBorder}`,
      borderRadius: tokens.radius.lg,
      maxWidth: "512px",
      maxHeight: "90vh",
      overflow: "auto",
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

const DialogHeader = ({ children, className = "", style = {}, ...props }) => (
  <div
    style={{
      padding: `${tokens.space.md}px`,
      borderBottom: `1px solid ${tokens.color.innerBorder}`,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

const DialogTitle = ({ children, className = "", style = {}, ...props }) => (
  <h2
    style={{
      fontSize: "12px",
      fontWeight: 500,
      color: tokens.color.text,
      ...style,
    }}
    {...props}
  >
    {children}
  </h2>
);

const DialogFooter = ({ children, className = "", style = {}, ...props }) => (
  <div
    style={{
      padding: `${tokens.space.md}px`,
      borderTop: `1px solid ${tokens.color.innerBorder}`,
      display: "flex",
      gap: `${tokens.space.sm}px`,
      justifyContent: "flex-end",
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

// Small green-tick / red-cross indicator used by the Duplicate Detector
// summary cards. Keeps the JSX terse while staying accessible.
const FieldBadge = ({ label, ok }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      borderRadius: "9999px",
      padding: "2px 8px",
      fontSize: "10px",
      fontWeight: 500,
      backgroundColor: ok ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
      color: ok ? tokens.color.green : tokens.color.red,
    }}
    data-testid={`field-badge-${label.toLowerCase().replace(/\s+/g, "-")}-${ok ? "ok" : "missing"}`}
  >
    {ok ? <Check style={{ width: "10px", height: "10px" }} /> : <X style={{ width: "10px", height: "10px" }} />}
    {label}
  </span>
);

export const AdminVenueManager = () => {
  const [query, setQuery] = useState("");
  const [venues, setVenues] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Edit modal state — populated when a venue row is clicked. Every field
  // on shared_venues is surfaced so the admin can hand-correct a record
  // without opening Mongo.
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);   // current editable values
  const [editOriginal, setEditOriginal] = useState(null); // snapshot for change-detection
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (v) => {
    const base = {
      name: v.name || "",
      city: v.city || "",
      country: v.country || "",
      address: v.address || "",
      postcode: v.postcode || "",
      website: v.website || "",
      capacity: v.capacity ?? "",
      lat: v.lat ?? "",
      lng: v.lng ?? "",
      place_id: v.place_id || "",
    };
    setEditForm({ venue_id: v.venue_id, ...base });
    setEditOriginal(base);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm?.venue_id || !editOriginal) return;
    setEditSaving(true);
    try {
      // Build a patch containing only the fields that changed. Coerce the
      // numeric ones back to numbers (or null when cleared). The backend's
      // PUT /admin/venues/{id} already handles re-geocoding when `address`
      // changes, so address edits trigger a fresh lat/lng lookup for free.
      const patch = {};
      for (const key of Object.keys(editOriginal)) {
        const before = editOriginal[key];
        const after = editForm[key];
        if (String(before ?? "") === String(after ?? "")) continue;
        if (["capacity", "lat", "lng"].includes(key)) {
          patch[key] = after === "" || after === null ? null : Number(after);
        } else {
          patch[key] = after;
        }
      }
      if (Object.keys(patch).length === 0) {
        toast("No changes to save");
        setEditOpen(false);
        return;
      }
      await apiClient.put(`/admin/venues/${editForm.venue_id}`, patch);
      toast.success("Venue updated");
      setEditOpen(false);
      search(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setEditSaving(false);
    }
  };

  const search = async (p = 1) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/venues?q=${encodeURIComponent(query)}&page=${p}&limit=20`);
      setVenues(res.data.venues || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (err) {
      toast.error("Failed to load venues");
    } finally {
      setLoading(false);
    }
  };

  const deleteVenue = async (vid) => {
    if (!window.confirm("Delete this venue?")) return;
    try {
      await apiClient.delete(`/admin/venues/${vid}`);
      toast.success("Venue deleted");
      search(page);
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <Card data-testid="admin-venue-manager">
      <CardHeader><CardTitle>Venue Database</CardTitle></CardHeader>
      <CardContent style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
        <div style={{ display: "flex", gap: `${tokens.space.sm}px` }}>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search venues..." />
          <Button size="sm" onClick={() => search(1)} disabled={loading}>
            {loading ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> : <Search style={{ width: "16px", height: "16px" }} />}
          </Button>
        </div>
        <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>{total} venues</p>
        <div style={{ maxHeight: "300px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
          {venues.map(v => (
            <div
              key={v.venue_id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: `${tokens.space.sm}px`,
                padding: `${tokens.space.sm}px`,
                borderRadius: tokens.radius.md,
                backgroundColor: tokens.color.innerBg,
                border: `1px solid ${tokens.color.innerBorder}`,
                fontSize: "10px",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(39, 39, 42, 0.8)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = tokens.color.innerBg)}
              onClick={() => openEdit(v)}
              data-testid={`admin-venue-row-${v.venue_id}`}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 500, color: tokens.color.text }}>{v.name}</span>
                <span style={{ color: tokens.color.textMuted, marginLeft: "8px" }}>{v.city}</span>
                {v.place_id && <Badge variant="outline" style={{ fontSize: "8px", marginLeft: "4px" }}>GPS</Badge>}
              </div>
              <Button
                variant="ghost"
                size="icon"
                style={{ width: "24px", height: "24px", color: tokens.color.red }}
                onClick={(e) => { e.stopPropagation(); deleteVenue(v.venue_id); }}
              >
                <Trash2 style={{ width: "12px", height: "12px" }} />
              </Button>
            </div>
          ))}
        </div>
        {total > 20 && (
          <div style={{ display: "flex", gap: `${tokens.space.sm}px`, justifyContent: "center" }}>
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => search(page - 1)}>Prev</Button>
            <span style={{ fontSize: "10px", color: tokens.color.textMuted, paddingTop: "8px", paddingBottom: "8px" }}>Page {page}</span>
            <Button variant="ghost" size="sm" onClick={() => search(page + 1)}>Next</Button>
          </div>
        )}

        {/* Edit modal — all fields inline, address change re-geocodes on save */}
        <Dialog open={editOpen} onOpenChange={(o) => { if (!editSaving) setEditOpen(o); }}>
          <DialogContent data-testid="admin-venue-edit-modal">
            <DialogHeader>
              <DialogTitle>Edit venue</DialogTitle>
            </DialogHeader>
            {editForm && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${tokens.space.md}px`, padding: `${tokens.space.md}px` }}>
                {[
                  ["name", "Name", "text"],
                  ["city", "City", "text"],
                  ["country", "Country", "text"],
                  ["address", "Address", "text"],
                  ["postcode", "Postcode", "text"],
                  ["website", "Website", "url"],
                  ["capacity", "Capacity", "number"],
                  ["lat", "Latitude", "number"],
                  ["lng", "Longitude", "number"],
                  ["place_id", "Google Place ID", "text"],
                ].map(([field, label, type]) => (
                  <div key={field} style={{ gridColumn: field === "address" || field === "place_id" ? "1 / -1" : "auto" }}>
                    <Label htmlFor={`venue-edit-${field}`} style={{ fontSize: "10px", color: tokens.color.textMuted }}>{label}</Label>
                    <Input
                      id={`venue-edit-${field}`}
                      type={type}
                      value={editForm[field] ?? ""}
                      onChange={(e) => setEditForm(s => ({ ...s, [field]: e.target.value }))}
                      data-testid={`admin-venue-edit-${field}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditOpen(false)}
                disabled={editSaving}
                data-testid="admin-venue-edit-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={saveEdit}
                disabled={editSaving}
                data-testid="admin-venue-edit-save"
              >
                {editSaving ? <><Loader2 style={{ width: "14px", height: "14px", marginRight: "4px", animation: "spin 1s linear infinite" }} />Saving…</> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export const AdminDuplicateDetector = () => {
  const [dupes, setDupes] = useState([]);
  const [loading, setLoading] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/venues/duplicates");
      setDupes(res.data.duplicates || []);
    } catch { toast.error("Scan failed"); }
    finally { setLoading(false); }
  };

  const merge = async (targetId, sourceId) => {
    try {
      await apiClient.post("/admin/venues/merge", { target_id: targetId, source_id: sourceId });
      toast.success("Merged!");
      setDupes(prev => prev.filter(d => d.venue_a.venue_id !== sourceId && d.venue_b.venue_id !== sourceId));
    } catch { toast.error("Merge failed"); }
  };

  return (
    <Card data-testid="admin-dupe-detector">
      <CardHeader style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CardTitle>Duplicate Detector</CardTitle>
        <Button size="sm" onClick={scan} disabled={loading}>
          {loading ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> : "Scan"}
        </Button>
      </CardHeader>
      <CardContent>
        {dupes.length === 0 ? (
          <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>{loading ? "Scanning..." : "No duplicates found. Click Scan."}</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px`, maxHeight: "480px", overflowY: "auto" }}>
            {dupes.map((d, i) => {
              const renderSide = (v) => (
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: tokens.radius.md,
                    border: `1px solid ${tokens.color.innerBorder}`,
                    backgroundColor: tokens.color.innerBg,
                    padding: `${tokens.space.sm}px`,
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                  data-testid={`dupe-card-${v.venue_id}`}
                >
                  <div style={{ display: "flex", alignItems: "baseline", gap: `${tokens.space.sm}px` }}>
                    <span style={{ fontWeight: 500, fontSize: "10px", overflow: "hidden", textOverflow: "ellipsis", color: tokens.color.text }}>{v.name}</span>
                    <span style={{ fontSize: "10px", color: tokens.color.textMuted, overflow: "hidden", textOverflow: "ellipsis" }}>{v.city}</span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    <FieldBadge label="GPS" ok={!!(v.lat && v.lng)} />
                    <FieldBadge label="Place ID" ok={!!v.place_id} />
                    <FieldBadge label="Postcode" ok={!!(v.postcode && String(v.postcode).trim())} />
                    <FieldBadge label="Website" ok={!!(v.website && String(v.website).trim())} />
                    <FieldBadge label="Capacity" ok={v.capacity != null && v.capacity !== ""} />
                  </div>
                  <div style={{ display: "flex", gap: `${tokens.space.md}px`, fontSize: "10px", color: tokens.color.textMuted }}>
                    <span data-testid={`dupe-gigs-${v.venue_id}`}>
                      <strong style={{ color: tokens.color.text }}>{v.gig_count ?? 0}</strong> gig{v.gig_count === 1 ? "" : "s"}
                    </span>
                    <span data-testid={`dupe-users-${v.venue_id}`}>
                      <strong style={{ color: tokens.color.text }}>{v.user_count ?? 0}</strong> user{v.user_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              );

              const hasGps = (venue) => !!(venue.lat && venue.lng);
              const keepWarning = (keeping) => {
                const gigCount = keeping.gig_count ?? 0;
                return `Keeping ${keeping.name} — ${gigCount} gig card${gigCount !== 1 ? "s" : ""} will ${hasGps(keeping) ? "retain" : "lose"} directions + leave-by time`;
              };

              return (
                <div
                  key={i}
                  style={{
                    padding: `${tokens.space.sm}px`,
                    borderRadius: tokens.radius.md,
                    backgroundColor: tokens.color.amberSoft,
                    border: "1px solid rgba(202, 138, 4, 0.3)",
                    display: "flex",
                    flexDirection: "column",
                    gap: `${tokens.space.sm}px`,
                  }}
                  data-testid={`dupe-pair-${i}`}
                >
                  <div style={{ display: "flex", alignItems: "stretch", gap: `${tokens.space.sm}px` }}>
                    {renderSide(d.venue_a)}
                    <div style={{ display: "flex", alignItems: "center", color: tokens.color.amber, fontSize: "10px", alignSelf: "center" }}>↔</div>
                    {renderSide(d.venue_b)}
                  </div>
                  <div style={{ display: "flex", flexDirection: "row", gap: "4px", fontSize: "10px", color: tokens.color.textMuted, fontStyle: "italic" }}>
                    <span style={{ flex: 1 }}>{keepWarning(d.venue_a)}</span>
                    <span style={{ flex: 1, textAlign: "right" }}>{keepWarning(d.venue_b)}</span>
                  </div>
                  <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      style={{ height: "24px", fontSize: "10px" }}
                      onClick={() => merge(d.venue_a.venue_id, d.venue_b.venue_id)}
                      data-testid={`dupe-keep-a-${i}`}
                    >
                      <Merge style={{ width: "12px", height: "12px", marginRight: "4px" }} />Keep A
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      style={{ height: "24px", fontSize: "10px" }}
                      onClick={() => merge(d.venue_b.venue_id, d.venue_a.venue_id)}
                      data-testid={`dupe-keep-b-${i}`}
                    >
                      <Merge style={{ width: "12px", height: "12px", marginRight: "4px" }} />Keep B
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const AdminSmartFillTest = () => {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const test = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await apiClient.post("/admin/smart-fill-test", { url });
      setResult(res.data.scrape_result);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scrape failed");
    } finally { setLoading(false); }
  };

  return (
    <Card data-testid="admin-smart-fill-test">
      <CardHeader><CardTitle>Smart Fill Tester</CardTitle></CardHeader>
      <CardContent style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
        <div style={{ display: "flex", gap: `${tokens.space.sm}px` }}>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste any event URL..." />
          <Button size="sm" onClick={test} disabled={loading || !url}>
            {loading ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> : <Globe style={{ width: "16px", height: "16px" }} />}
          </Button>
        </div>
        {result && (
          <div style={{ backgroundColor: tokens.color.innerBg, border: `1px solid ${tokens.color.innerBorder}`, borderRadius: tokens.radius.md, padding: `${tokens.space.md}px`, display: "flex", flexDirection: "column", gap: "4px", maxHeight: "250px", overflowY: "auto" }}>
            {Object.entries(result).filter(([_, v]) => v != null).map(([k, v]) => (
              <div key={k} style={{ display: "flex", gap: `${tokens.space.sm}px`, fontSize: "10px" }}>
                <span style={{ color: tokens.color.textMuted, width: "112px", flexShrink: 0, fontFamily: "monospace" }}>{k}:</span>
                <span style={{ color: tokens.color.text, wordBreak: "break-all" }}>{String(v)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const AdminSubscribers = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/subscribers");
      setSubs(res.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const togglePro = async (userId, currentlyPro) => {
    try {
      await apiClient.post(`/admin/users/${userId}/grant-pro`, { grant: !currentlyPro });
      toast.success(currentlyPro ? "Pro revoked" : "Pro granted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <Card data-testid="admin-subscribers">
      <CardHeader style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CardTitle>Stripe / Pro Users</CardTitle>
        <Button size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> : "Load"}
        </Button>
      </CardHeader>
      <CardContent>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "200px", overflowY: "auto" }}>
          {subs.map(s => (
            <div key={s.user_id} style={{ display: "flex", alignItems: "center", gap: `${tokens.space.sm}px`, padding: `${tokens.space.sm}px`, borderRadius: tokens.radius.md, backgroundColor: tokens.color.innerBg, border: `1px solid ${tokens.color.innerBorder}`, fontSize: "10px" }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", color: tokens.color.text }}>{s.email}</span>
              <Badge style={{ fontSize: "10px" }}>{s.lifetime_access ? "Lifetime" : s.plan_type}</Badge>
              <Button variant="ghost" size="sm" style={{ height: "20px", fontSize: "10px", color: tokens.color.red }} onClick={() => togglePro(s.user_id, true)}>
                Revoke
              </Button>
            </div>
          ))}
          {subs.length === 0 && !loading && <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>Click Load to view subscribers</p>}
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminGrantLifetime = () => {
  const [grantEmail, setGrantEmail] = useState("");
  const [showGrant, setShowGrant] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGrantPro = async () => {
    if (!grantEmail) return;
    setLoading(true);
    try {
      await apiClient.post("/subscription/grant-pro", { email: grantEmail });
      toast.success(`Lifetime Pro granted to ${grantEmail}`);
      setGrantEmail("");
      setShowGrant(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant access");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="admin-grant-lifetime">
      <CardHeader>
        <CardTitle style={{ display: "flex", alignItems: "center", gap: `${tokens.space.sm}px` }}>
          <Crown style={{ width: "16px", height: "16px", color: tokens.color.orange }} /> Grant Lifetime Access
        </CardTitle>
      </CardHeader>
      <CardContent>
        <button
          onClick={() => setShowGrant(!showGrant)}
          style={{ fontSize: "10px", color: tokens.color.textMuted, background: "none", border: "none", cursor: "pointer", padding: 0, transition: "color 0.2s" }}
          onMouseEnter={(e) => (e.target.style.color = tokens.color.orange)}
          onMouseLeave={(e) => (e.target.style.color = tokens.color.textMuted)}
          data-testid="grant-pro-toggle"
        >
          Grant lifetime Pro to a user
        </button>
        {showGrant && (
          <div style={{ marginTop: `${tokens.space.sm}px`, display: "flex", gap: `${tokens.space.sm}px` }}>
            <Input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              data-testid="grant-pro-email"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={loading || !grantEmail}
              onClick={handleGrantPro}
              data-testid="grant-pro-btn"
            >
              {loading ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> : "Grant"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// One-time maintenance action: recompute every gig's stored travel-distance
// estimate (any user) from their current home address. Fixes gigs that
// picked up a stale/wrong figure before the client/server write-conflict fix
// — new gigs going forward don't need this, so it's an admin action rather
// than a self-service button.
export const AdminRecomputeTravelEstimates = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await apiClient.post("/admin/gigs/recompute-travel-estimates");
      setResult(res.data);
      toast.success(`Recomputed ${res.data.recomputed} gig(s) across ${res.data.users_affected} user(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recompute failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card data-testid="admin-recompute-travel-estimates">
      <CardHeader>
        <CardTitle style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <RefreshCw style={{ width: "16px", height: "16px" }} /> Recompute Gig Travel Distances
        </CardTitle>
        <p style={{ margin: "4px 0 0", fontSize: "10px", color: tokens.color.textMuted }}>
          Recalculates estimated_distance_miles for every gig (any user with a home address set),
          using their current home address. One-time cleanup for gigs that picked up a stale/wrong
          figure before the write-conflict fix — not something new users should hit.
        </p>
      </CardHeader>
      <CardContent style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.sm}px` }}>
        <Button size="sm" onClick={run} disabled={running}>
          {running ? (
            <><Loader2 style={{ width: "14px", height: "14px", marginRight: "4px", animation: "spin 1s linear infinite" }} />Recomputing…</>
          ) : (
            "Recompute All Users' Gig Distances"
          )}
        </Button>
        {result && (
          <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>
            Recomputed {result.recomputed} gig(s) across {result.users_affected} user(s) with a home address set.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/analytics");
      setData(res.data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };

  return (
    <Card data-testid="admin-analytics">
      <CardHeader style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CardTitle>Gig Analytics</CardTitle>
        <Button size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> : <BarChart3 style={{ width: "16px", height: "16px" }} />}
        </Button>
      </CardHeader>
      <CardContent>
        {data ? (
          <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: `${tokens.space.sm}px`, textAlign: "center" }}>
              <div style={{ padding: `${tokens.space.sm}px`, borderRadius: tokens.radius.md, backgroundColor: "rgba(245, 137, 31, 0.2)", border: "1px solid rgba(245, 137, 31, 0.3)" }}>
                <p style={{ fontSize: "18px", fontWeight: "bold", color: tokens.color.orange }}>{data.total_gigs}</p>
                <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>Total Gigs</p>
              </div>
              <div style={{ padding: tokens.space.sm, borderRadius: tokens.radius.md, backgroundColor: "rgba(16, 185, 129, 0.2)", border: "1px solid rgba(16, 185, 129, 0.3)" }}>
                <p style={{ fontSize: "18px", fontWeight: "bold", color: tokens.color.green }}>{data.total_users}</p>
                <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>Users</p>
              </div>
              <div style={{ padding: tokens.space.sm, borderRadius: tokens.radius.md, backgroundColor: "rgba(217, 119, 6, 0.2)", border: "1px solid rgba(217, 119, 6, 0.3)" }}>
                <p style={{ fontSize: "18px", fontWeight: "bold", color: tokens.color.amber }}>{data.total_venues}</p>
                <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>Venues</p>
              </div>
            </div>
            {data.popular_venues?.length > 0 && (
              <div>
                <p style={{ fontSize: "10px", fontWeight: 500, marginBottom: "4px", color: tokens.color.text }}>Top Venues</p>
                {data.popular_venues.slice(0, 5).map((v, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", paddingTop: "4px", paddingBottom: "4px" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", color: tokens.color.text }}>{v.name}</span>
                    <span style={{ color: tokens.color.textMuted }}>{v.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p style={{ fontSize: "10px", color: tokens.color.textMuted }}>Click Load to view analytics</p>
        )}
      </CardContent>
    </Card>
  );
};

export const AdminInvitationEmailPreview = () => {
  const [showPreview, setShowPreview] = useState(false);

  const testGig = {
    artist_name: "The Brass Collective",
    venue_name: "Blue Note Jazz Club",
    venue_address: "123 Blue Lane",
    city: "London",
    date: "2026-07-15",
    start_time: "20:00",
    call_time: "19:30",
    dress_code: "Smart casual",
    gig_notes: "This is a special summer series gig. Bring your own music stand. Sound check at 19:45.",
    userName: "Freddie Gavita"
  };

  const testToken = "abc123xyz789test";

  const emailHTML = `
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#1a1a1c;padding:40px 16px;">
      <tr><td align="center">
        <table width="100%" maxwidth="600" cellpadding="0" cellspacing="0" style="max-width:600px;background:#0c0c0d;border:1px solid #262628;border-radius:14px;overflow:hidden;">
          <tr><td style="padding:20px 28px;border-bottom:1px solid #262628;background:#0f0f10;">
            <div style="font-family:'Geist',system-ui,sans-serif;font-size:17px;font-weight:700;color:#fff;">Fretless</div>
          </td></tr>
          <tr><td style="padding:40px 36px 28px;color:#f1efe9;font-family:'Geist',system-ui,sans-serif;">
            <div style="font-family:'Fraunces',Georgia,serif;font-size:42px;font-weight:600;line-height:1.05;margin:0 0 20px;color:#f1efe9;">A gig with<br/>your name on it.</div>
            <p style="margin:0;font-size:15.5px;color:#a2a09a;">
              <span style="color:#f1efe9;font-weight:600;">${testGig.userName}</span> has invited you to a gig on Fretless.
            </p>
          </td></tr>
          <tr><td style="padding:0 36px;font-family:'Geist',system-ui,sans-serif;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#0f0f10;border:1px solid #262628;border-radius:12px;">
              <tr><td style="padding:22px 22px 18px;border-bottom:1px solid #262628;">
                <div style="font-family:'Geist Mono',monospace;font-size:10px;text-transform:uppercase;color:#f5891f;">◆ The gig</div>
                <div style="font-family:'Fraunces',Georgia,serif;font-size:26px;font-weight:600;color:#fff;margin:0;word-wrap:break-word;">${testGig.artist_name}</div>
              </td></tr>
              <tr><td>
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td style="padding:16px 20px;border-right:1px solid #262628;border-bottom:1px solid #262628;width:50%;">
                      <div style="font-family:'Geist Mono',monospace;font-size:10px;text-transform:uppercase;color:#6c6a65;">When</div>
                      <div style="font-size:17px;font-weight:600;color:#fff;margin:0;">2026-07-15</div>
                      <div style="font-size:13px;color:#a2a09a;margin:0;">20:00</div>
                    </td>
                    <td style="padding:16px 20px;border-bottom:1px solid #262628;width:50%;">
                      <div style="font-family:'Geist Mono',monospace;font-size:10px;text-transform:uppercase;color:#6c6a65;">Where</div>
                      <div style="font-size:17px;font-weight:600;color:#fff;margin:0;">Blue Note Jazz Club, London</div>
                    </td>
                  </tr>
                </table>
              </td></tr>
            </table>
          </td></tr>
          <tr><td style="padding:28px 36px 16px;">
            <div style="font-family:'Geist Mono',monospace;font-size:11px;text-transform:uppercase;color:#6c6a65;margin-bottom:12px;">Confirm availability</div>
            <table width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td style="padding-right:5px;width:50%;">
                  <a href="#" style="display:block;text-align:center;padding:14px;background:transparent;color:#f1efe9;text-decoration:none;border:1px solid #3a3a3c;border-radius:10px;font-size:15px;font-weight:600;font-family:'Geist',system-ui,sans-serif;">
                    <span style="color:#f5891f;font-weight:700;margin-right:8px;">✓</span>I'm in
                  </a>
                </td>
                <td style="padding-left:5px;width:50%;">
                  <a href="#" style="display:block;text-align:center;padding:14px;background:transparent;color:#a2a09a;text-decoration:none;border:1px solid #262628;border-radius:10px;font-size:15px;font-family:'Geist',system-ui,sans-serif;">
                    <span style="font-weight:600;margin-right:8px;">✕</span>Not free
                  </a>
                </td>
              </tr>
            </table>
          </td></tr>
          <tr><td style="padding:16px 36px 8px;">
            <a href="#" style="display:block;padding:18px 22px;background:#f5891f;color:#0c0500;border-radius:10px;text-decoration:none;font-family:'Geist',system-ui,sans-serif;text-align:center;">
              <div style="font-family:'Geist Mono',monospace;font-size:10px;text-transform:uppercase;opacity:0.7;">View on Fretless</div>
              <div style="font-size:19px;font-weight:700;margin:0;">See more details →</div>
            </a>
          </td></tr>
          <tr><td style="padding:18px 28px 20px;border-top:1px solid #262628;background:#161617;font-family:'Geist',system-ui,sans-serif;">
            <p style="font-size:12px;color:#6c6a65;margin:0;">Sent by ${testGig.userName} using <span style="color:#f1efe9;font-weight:600;">Fretless</span></p>
          </td></tr>
        </table>
      </td></tr>
    </table>
  `;

  return (
    <Card data-testid="admin-invitation-email-preview">
      <CardHeader>
        <CardTitle style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Mail style={{ width: "16px", height: "16px" }} /> Invitation Email
        </CardTitle>
        <p style={{ margin: "4px 0 0", fontSize: "10px", color: tokens.color.textMuted }}>
          What invitees receive by email when you invite them to a gig.
        </p>
      </CardHeader>
      <CardContent>
        <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
          {showPreview ? "Hide Invitation Email Preview" : "View Invitation Email Preview"}
        </Button>
        {showPreview && (
          <div style={{
            marginTop: tokens.space.md,
            backgroundColor: tokens.color.innerBg,
            borderRadius: tokens.radius.md,
            border: `1px solid ${tokens.color.innerBorder}`,
            overflow: "hidden",
            height: "600px",
            display: "flex",
            flexDirection: "column"
          }}>
            <iframe
              srcDoc={emailHTML}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                backgroundColor: "#1a1a1c"
              }}
              title="Email Preview"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
